/**
 * Control Portal - Device Control Panel
 * Individual device management with full controls
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { controlApi } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowLeft, Wifi, WifiOff, Battery, Thermometer, MapPin, 
  RefreshCw, Loader2, Play, Pause, XCircle, Plus, Building2,
  Calendar, Shield, Clock, Settings, Zap
} from 'lucide-react';

export default function DeviceDetailPage() {
  const { deviceId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [device, setDevice] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [locations, setLocations] = useState([]);
  const [plans, setPlans] = useState([]);
  const [features, setFeatures] = useState(null);
  
  // Dialog states
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [activateDialogOpen, setActivateDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Form data
  const [assignForm, setAssignForm] = useState({ tenant_id: '', location_id: '' });
  const [activateForm, setActivateForm] = useState({
    plan_code: 'STANDARD',
    license_days: 365,
    grace_days: 7,
    allow_temp: true,
    allow_weight: false
  });

  const fetchDevice = useCallback(async () => {
    try {
      setLoading(true);
      const [deviceRes, tenantsRes, plansRes, featuresRes] = await Promise.all([
        controlApi.getGlobalDeviceDetail(deviceId),
        controlApi.getTenants(),
        controlApi.getPlans({ active_only: true }),
        controlApi.getDeviceFeatures(deviceId)
      ]);
      setDevice(deviceRes.data);
      setTenants(tenantsRes.data);
      setPlans(plansRes.data);
      setFeatures(featuresRes.data);
      
      if (deviceRes.data.tenant?.id) {
        setAssignForm(prev => ({ ...prev, tenant_id: deviceRes.data.tenant.id }));
        // Fetch locations for current tenant
        const locRes = await controlApi.getLocations(deviceRes.data.tenant.id, { limit: 200 });
        setLocations(locRes.data);
      }
    } catch (error) {
      toast.error('Failed to load device');
    } finally {
      setLoading(false);
    }
  }, [deviceId]);

  useEffect(() => {
    fetchDevice();
  }, [fetchDevice]);

  const handleTenantChange = async (tenantId) => {
    setAssignForm({ ...assignForm, tenant_id: tenantId, location_id: '' });
    if (tenantId) {
      try {
        const locRes = await controlApi.getLocations(tenantId, { limit: 200 });
        setLocations(locRes.data);
      } catch (error) {
        setLocations([]);
      }
    } else {
      setLocations([]);
    }
  };

  const handleAssign = async () => {
    if (!assignForm.tenant_id) {
      toast.error('Select a tenant');
      return;
    }
    setSaving(true);
    try {
      await controlApi.assignDevice(deviceId, {
        tenant_id: assignForm.tenant_id,
        location_id: assignForm.location_id || null
      });
      toast.success('Device assigned successfully');
      setAssignDialogOpen(false);
      fetchDevice();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign device');
    } finally {
      setSaving(false);
    }
  };

  const handleActivate = async () => {
    setSaving(true);
    try {
      await controlApi.activateDevice(deviceId, activateForm);
      toast.success('Device activated with license');
      setActivateDialogOpen(false);
      fetchDevice();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to activate device');
    } finally {
      setSaving(false);
    }
  };

  const handleSuspend = async () => {
    if (!confirm('Suspend this device?')) return;
    try {
      await controlApi.suspendGlobalDevice(deviceId);
      toast.success('Device suspended');
      fetchDevice();
    } catch (error) {
      toast.error('Failed to suspend device');
    }
  };

  const handleResume = async () => {
    try {
      await controlApi.resumeGlobalDevice(deviceId);
      toast.success('Device resumed');
      fetchDevice();
    } catch (error) {
      toast.error('Failed to resume device');
    }
  };

  const handleDeactivate = async () => {
    if (!confirm('Deactivate this device? This will revoke its license.')) return;
    try {
      await controlApi.deactivateDevice(deviceId);
      toast.success('Device deactivated');
      fetchDevice();
    } catch (error) {
      toast.error('Failed to deactivate device');
    }
  };

  const activeLocations = useMemo(() => locations.filter(l => l.status === 'active'), [locations]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!device) {
    return (
      <div className="p-6 text-center">
        <p className="text-slate-400">Device not found</p>
        <Button onClick={() => navigate('/control/devices')} className="mt-4">
          Back to Inventory
        </Button>
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="device-control-panel">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/control/devices')}
            className="text-slate-400"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              device.is_online ? 'bg-green-500/20' : 'bg-slate-700'
            }`}>
              {device.is_online ? (
                <Wifi className="w-6 h-6 text-green-400" />
              ) : (
                <WifiOff className="w-6 h-6 text-slate-400" />
              )}
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white font-mono">{device.device_uid}</h1>
              <p className="text-slate-400">{device.imei_number || 'No IMEI'}</p>
            </div>
          </div>
          <Badge className={
            device.status === 'active' ? 'bg-green-500/20 text-green-400' :
            device.status === 'suspended' ? 'bg-amber-500/20 text-amber-400' :
            device.status === 'revoked' ? 'bg-red-500/20 text-red-400' :
            'bg-slate-500/20 text-slate-400'
          }>
            {device.status}
          </Badge>
          {/* Feature Badges */}
          {features && (
            <div className="flex gap-1 ml-2">
              {features.gps.enabled && (
                <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">
                  <MapPin className="w-3 h-3 mr-1" />GPS
                </Badge>
              )}
              {features.temp.enabled && (
                <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">
                  <Thermometer className="w-3 h-3 mr-1" />TEMP
                </Badge>
              )}
              {features.weight.enabled && (
                <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                  <Shield className="w-3 h-3 mr-1" />WEIGHT
                </Badge>
              )}
            </div>
          )}
        </div>
        <Button variant="outline" onClick={fetchDevice} className="border-slate-700 text-slate-300">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Button
          onClick={() => setAssignDialogOpen(true)}
          className="h-auto py-4 bg-blue-600 hover:bg-blue-700"
        >
          <div className="text-center">
            <Building2 className="w-5 h-5 mx-auto mb-1" />
            <span>Assign Tenant</span>
          </div>
        </Button>
        <Button
          onClick={() => setActivateDialogOpen(true)}
          className="h-auto py-4 bg-green-600 hover:bg-green-700"
        >
          <div className="text-center">
            <Zap className="w-5 h-5 mx-auto mb-1" />
            <span>Activate License</span>
          </div>
        </Button>
        {device.status === 'active' ? (
          <Button
            onClick={handleSuspend}
            variant="outline"
            className="h-auto py-4 border-amber-500 text-amber-400 hover:bg-amber-500/20"
          >
            <div className="text-center">
              <Pause className="w-5 h-5 mx-auto mb-1" />
              <span>Suspend</span>
            </div>
          </Button>
        ) : device.status === 'suspended' ? (
          <Button
            onClick={handleResume}
            variant="outline"
            className="h-auto py-4 border-green-500 text-green-400 hover:bg-green-500/20"
          >
            <div className="text-center">
              <Play className="w-5 h-5 mx-auto mb-1" />
              <span>Resume</span>
            </div>
          </Button>
        ) : (
          <Button
            disabled
            variant="outline"
            className="h-auto py-4 border-slate-700 text-slate-500"
          >
            <div className="text-center">
              <Pause className="w-5 h-5 mx-auto mb-1" />
              <span>N/A</span>
            </div>
          </Button>
        )}
        <Button
          onClick={handleDeactivate}
          variant="outline"
          className="h-auto py-4 border-red-500 text-red-400 hover:bg-red-500/20"
          disabled={device.status === 'revoked'}
        >
          <div className="text-center">
            <XCircle className="w-5 h-5 mx-auto mb-1" />
            <span>Deactivate</span>
          </div>
        </Button>
      </div>

      {/* Info Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Device Info */}
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Settings className="w-5 h-5 text-blue-400" />
            Device Information
          </h3>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-400">Device UID</Label>
                <p className="text-white font-mono">{device.device_uid}</p>
              </div>
              <div>
                <Label className="text-slate-400">IMEI</Label>
                <p className="text-white">{device.imei_number || '-'}</p>
              </div>
              <div>
                <Label className="text-slate-400">SIM Number</Label>
                <p className="text-white">{device.sim_number || '-'}</p>
              </div>
              <div>
                <Label className="text-slate-400">Firmware</Label>
                <p className="text-white">{device.firmware_version || '-'}</p>
              </div>
            </div>
            <div className="border-t border-slate-800 pt-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-3 bg-slate-800 rounded-lg">
                  <Thermometer className="w-5 h-5 mx-auto mb-1 text-blue-400" />
                  <p className="text-white font-bold">{device.current_temp || '-'}°C</p>
                  <p className="text-slate-500 text-xs">Temperature</p>
                </div>
                <div className="text-center p-3 bg-slate-800 rounded-lg">
                  <Battery className={`w-5 h-5 mx-auto mb-1 ${
                    device.battery_level > 50 ? 'text-green-400' :
                    device.battery_level > 20 ? 'text-amber-400' : 'text-red-400'
                  }`} />
                  <p className="text-white font-bold">{device.battery_level || 0}%</p>
                  <p className="text-slate-500 text-xs">Battery</p>
                </div>
                <div className="text-center p-3 bg-slate-800 rounded-lg">
                  <Clock className="w-5 h-5 mx-auto mb-1 text-slate-400" />
                  <p className="text-white font-bold text-sm">
                    {device.last_seen_at ? new Date(device.last_seen_at).toLocaleTimeString() : '-'}
                  </p>
                  <p className="text-slate-500 text-xs">Last Seen</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Assignment Info */}
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-green-400" />
            Assignment
          </h3>
          <div className="space-y-4">
            <div>
              <Label className="text-slate-400">Tenant</Label>
              {device.tenant ? (
                <div className="flex items-center gap-2 mt-1">
                  <Badge variant="outline" className="border-slate-600">{device.tenant.code}</Badge>
                  <span className="text-white">{device.tenant.name}</span>
                </div>
              ) : (
                <p className="text-amber-400 mt-1">Not assigned to any tenant</p>
              )}
            </div>
            <div>
              <Label className="text-slate-400">Location</Label>
              {device.location ? (
                <div className="flex items-center gap-2 mt-1">
                  <MapPin className="w-4 h-4 text-slate-500" />
                  <span className="text-white">{device.location.name}</span>
                  <Badge variant="outline" className="border-slate-600 text-xs">{device.location.type}</Badge>
                </div>
              ) : (
                <p className="text-slate-500 mt-1">No location assigned</p>
              )}
            </div>
          </div>
        </div>

        {/* License Info */}
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-6 lg:col-span-2">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Shield className="w-5 h-5 text-amber-400" />
            License / Entitlement
          </h3>
          {device.entitlement ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <Label className="text-slate-400">Plan</Label>
                <Badge className="mt-1 bg-blue-500/20 text-blue-400 border-blue-500/30">
                  {device.entitlement.plan_code}
                </Badge>
              </div>
              <div>
                <Label className="text-slate-400">Status</Label>
                <Badge className={`mt-1 ${
                  device.entitlement.status === 'active' ? 'bg-green-500/20 text-green-400' :
                  device.entitlement.status === 'suspended' ? 'bg-amber-500/20 text-amber-400' :
                  'bg-red-500/20 text-red-400'
                }`}>
                  {device.entitlement.status}
                </Badge>
              </div>
              <div>
                <Label className="text-slate-400">Expires</Label>
                <p className="text-white mt-1">
                  {device.entitlement.expires_at
                    ? new Date(device.entitlement.expires_at).toLocaleDateString()
                    : '-'}
                </p>
              </div>
              <div>
                <Label className="text-slate-400">Days Remaining</Label>
                <p className={`text-xl font-bold mt-1 ${
                  device.entitlement.days_remaining <= 7 ? 'text-red-400' :
                  device.entitlement.days_remaining <= 30 ? 'text-amber-400' : 'text-green-400'
                }`}>
                  {device.entitlement.days_remaining ?? '-'}d
                </p>
              </div>
              <div>
                <Label className="text-slate-400">Grace Period</Label>
                <p className="text-white mt-1">{device.entitlement.grace_days} days</p>
              </div>
              <div>
                <Label className="text-slate-400">Temperature</Label>
                <Badge variant={device.entitlement.allow_temp ? 'default' : 'secondary'} className="mt-1">
                  {device.entitlement.allow_temp ? 'Enabled' : 'Disabled'}
                </Badge>
              </div>
              <div>
                <Label className="text-slate-400">Weight</Label>
                <Badge variant={device.entitlement.allow_weight ? 'default' : 'secondary'} className="mt-1">
                  {device.entitlement.allow_weight ? 'Enabled' : 'Disabled'}
                </Badge>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <Shield className="w-12 h-12 mx-auto mb-3 text-slate-600" />
              <p className="text-slate-400 mb-4">No license assigned to this device</p>
              <Button onClick={() => setActivateDialogOpen(true)} className="bg-green-600 hover:bg-green-700">
                <Zap className="w-4 h-4 mr-2" />
                Activate License
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Assign Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Assign Device to Tenant</DialogTitle>
            <DialogDescription className="text-slate-400">
              Transfer this device to a customer tenant
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-slate-300">Target Tenant *</Label>
              <Select value={assignForm.tenant_id} onValueChange={handleTenantChange}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white mt-1">
                  <SelectValue placeholder="Select tenant" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {tenants.map(t => (
                    <SelectItem key={t.id} value={t.id} className="text-white">{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-300">Location (optional)</Label>
              <Select
                value={assignForm.location_id}
                onValueChange={(v) => setAssignForm({ ...assignForm, location_id: v })}
                disabled={!assignForm.tenant_id || locations.length === 0}
              >
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white mt-1">
                  <SelectValue placeholder="Select location" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700 max-h-[200px]">
                  {activeLocations.map(loc => (
                    <SelectItem key={loc.id} value={loc.id} className="text-white">
                      {loc.name} ({loc.type})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2 pt-4">
              <Button variant="ghost" onClick={() => setAssignDialogOpen(false)} className="flex-1">
                Cancel
              </Button>
              <Button onClick={handleAssign} disabled={saving} className="flex-1 bg-blue-600 hover:bg-blue-700">
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Assign
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Activate Dialog */}
      <Dialog open={activateDialogOpen} onOpenChange={setActivateDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Activate Device License</DialogTitle>
            <DialogDescription className="text-slate-400">
              Set up license plan for this device
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-slate-300">Plan</Label>
              <Select
                value={activateForm.plan_code}
                onValueChange={(v) => setActivateForm({ ...activateForm, plan_code: v })}
              >
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="BASIC" className="text-white">Basic</SelectItem>
                  <SelectItem value="STANDARD" className="text-white">Standard</SelectItem>
                  <SelectItem value="ENTERPRISE" className="text-white">Enterprise</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">License Duration (days)</Label>
                <Input
                  type="number"
                  value={activateForm.license_days}
                  onChange={(e) => setActivateForm({ ...activateForm, license_days: parseInt(e.target.value) || 0 })}
                  className="bg-slate-800 border-slate-700 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-slate-300">Grace Period (days)</Label>
                <Input
                  type="number"
                  value={activateForm.grace_days}
                  onChange={(e) => setActivateForm({ ...activateForm, grace_days: parseInt(e.target.value) || 0 })}
                  className="bg-slate-800 border-slate-700 text-white mt-1"
                />
              </div>
            </div>
            <div className="flex gap-4">
              {[30, 90, 180, 365].map((days) => (
                <Button
                  key={days}
                  variant="outline"
                  size="sm"
                  onClick={() => setActivateForm({ ...activateForm, license_days: days })}
                  className={`border-slate-700 ${activateForm.license_days === days ? 'bg-blue-600 text-white' : 'text-slate-300'}`}
                >
                  {days}d
                </Button>
              ))}
            </div>
            <div className="flex gap-2 pt-4">
              <Button variant="ghost" onClick={() => setActivateDialogOpen(false)} className="flex-1">
                Cancel
              </Button>
              <Button onClick={handleActivate} disabled={saving} className="flex-1 bg-green-600 hover:bg-green-700">
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Activate
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
